import React from 'react';
import { FaCheckCircle, FaLock, FaUndo } from 'react-icons/fa';
import { formatCurrency } from '../../utils/cartTotals';

const TRUST_BADGES = [
  { Icon: FaLock,        label: '100% Secure Payments' },
  { Icon: FaUndo,        label: 'Easy Returns & Refunds' },
  { Icon: FaCheckCircle, label: 'Fresh Quality Guaranteed' },
];

const getItemKey = (item) =>
  `${item.product_id}-${item.product_variant_id ?? 'base'}-${item.variant_id ?? 'base'}`;

// Best line total: server item_total → price × qty
const lineTotal = (item) => {
  if (item.item_total && parseFloat(item.item_total) > 0) return parseFloat(item.item_total);
  return parseFloat(item.price || 0) * parseInt(item.quantity || 1, 10);
};

const OrderSummary = ({ cart, subtotal, delivery, gstTotal, total }) => (
  <div className="sticky top-6 rounded-3xl border border-gray-100 bg-white p-6 shadow-xl shadow-black/5">
    <h2 className="mb-5 text-lg font-extrabold text-gray-900">Order Summary</h2>

    {/* Dynamic item list from cart state */}
    <div className="mb-5 max-h-56 space-y-3 overflow-y-auto pr-1">
      {cart.length === 0 ? (
        <p className="text-xs text-gray-400 italic">No items</p>
      ) : (
        cart.map((item) => (
          <div key={getItemKey(item)} className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gray-50">
              {item.image ? (
                <img src={item.image} alt={item.name} className="h-full w-full object-contain" />
              ) : (
                <span className="text-[10px] text-gray-400">img</span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold text-gray-800">{item.name}</p>
              <p className="text-[11px] text-gray-400">
                x{item.quantity}
                {item.variant ? ` · ${item.variant}` : ''}
              </p>
            </div>
            <p className="shrink-0 text-xs font-bold text-gray-800">
              {formatCurrency(lineTotal(item))}
            </p>
          </div>
        ))
      )}
    </div>

    {/* Totals — all from server via props */}
    <div className="space-y-3 border-t border-gray-100 pt-4 text-sm">
      <div className="flex justify-between text-gray-600">
        <span>Subtotal</span>
        <span className="font-semibold text-gray-800">{formatCurrency(subtotal)}</span>
      </div>
      {gstTotal > 0 && (
        <div className="flex justify-between text-gray-600">
          <span>GST / Tax</span>
          <span className="font-semibold text-gray-800">{formatCurrency(gstTotal)}</span>
        </div>
      )}
      <div className="flex justify-between text-gray-600">
        <span>Delivery</span>
        <span className={`font-semibold ${delivery === 0 ? 'text-green-600' : 'text-gray-800'}`}>
          {delivery === 0 ? 'FREE' : formatCurrency(delivery)}
        </span>
      </div>
      <div className="flex justify-between border-t border-gray-100 pt-3 text-base font-extrabold text-gray-900">
        <span>Total</span>
        <span className="text-primary-600">{formatCurrency(total)}</span>
      </div>
    </div>

    <div className="mt-5 space-y-2 border-t border-gray-100 pt-4">
      {TRUST_BADGES.map(({ Icon, label }) => (
        <div key={label} className="flex items-center gap-2 text-xs font-medium text-gray-500">
          <Icon className="h-3.5 w-3.5 shrink-0 text-primary-600" aria-hidden="true" />
          <span>{label}</span>
        </div>
      ))}
    </div>
  </div>
);

export default OrderSummary;
